/**
 * questionnaire-field-visibility - shared utilities for evaluating which
 * questionnaire fields are visible based on current answer state.
 *
 * Extracted from DealPage.tsx so that useDealCoaching can use the same
 * visibility logic when building the tracked-questions panel.
 *
 * These helpers are keyed by fieldKey (not templateFieldId) so they work
 * regardless of whether the caller has a fieldLookup available.
 */

import { normalizeCurrencyValue, parseTechStackSystems } from '@protoss/shared';
import type {
  ConditionalRule,
  SalesDealQuestionnaireSummary,
  SalesDealTemplateFieldSummary,
} from '../services/salesApi';

// ── Types ──────────────────────────────────────────────────────────────────

export type QuestionnaireAnswerMap = Record<string, unknown>;

export type QuestionnaireFieldLookup = Map<string, SalesDealTemplateFieldSummary>;

// ── Helpers ────────────────────────────────────────────────────────────────

function getAnswerKey(field: SalesDealTemplateFieldSummary): string {
  return field.templateFieldId !== null ? String(field.templateFieldId) : field.fieldKey;
}

// ── Exported utilities ─────────────────────────────────────────────────────

export function buildQuestionnaireFieldLookup(
  questionnaire: SalesDealQuestionnaireSummary | null
): QuestionnaireFieldLookup {
  const lookup: QuestionnaireFieldLookup = new Map();

  if (!questionnaire) {
    return lookup;
  }

  for (const section of questionnaire.sections) {
    for (const question of section.groups) {
      for (const field of question.fields) {
        lookup.set(field.fieldKey, field);
      }
    }
  }

  for (const field of questionnaire.headerFields ?? []) {
    lookup.set(field.fieldKey, field);
  }

  return lookup;
}

export function getAnswerValueByFieldKey(
  fieldKey: string,
  answers: QuestionnaireAnswerMap,
  fieldLookup?: QuestionnaireFieldLookup
): unknown {
  const field = fieldLookup?.get(fieldKey);

  if (field) {
    return answers[getAnswerKey(field)] ?? null;
  }

  return answers[fieldKey] ?? null;
}

export function normalizeAnswerChoice(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export function normalizeMultiSelectValue(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string');
  return [];
}

export function normalizeTextValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

/**
 * Canonical per-field value for dirty/savable comparison (drives the per-section Save button).
 * Each uiControl maps to a comparable shape; the default text path returns '' for non-string
 * values, so structured controls (table, tech_stack_editor, ...) MUST have an explicit branch -
 * otherwise their array value normalizes to null on both sides and a real change never registers.
 */
export function normalizeQuestionnaireDirtyValue(
  field: SalesDealTemplateFieldSummary,
  value: unknown
): unknown {
  if (
    field.uiControl === 'multi_select' ||
    field.uiControl === 'pill_select' ||
    field.uiControl === 'pill_dropdown' ||
    field.uiControl === 'tag_input'
  ) {
    if (field.displayHints?.annotation && Array.isArray(value)) {
      // Annotated multi_select stores objects { value, [key]: string }.
      const items = (value as unknown[])
        .filter(
          (v): v is Record<string, string> => typeof v === 'object' && v !== null && 'value' in v
        )
        .sort((a, b) => String(a.value).localeCompare(String(b.value)));
      return items.length > 0 ? items : null;
    }
    const normalized = normalizeMultiSelectValue(value).slice().sort();
    return normalized.length > 0 ? normalized : null;
  }

  if (field.uiControl === 'currency') {
    return normalizeCurrencyValue(value);
  }

  if (field.uiControl === 'number' || field.uiControl === 'slider') {
    if (typeof value === 'number') {
      return Number.isNaN(value) ? null : value;
    }
    if (typeof value === 'string') {
      const trimmed = value.replace(/,/g, '').trim();
      if (!trimmed) return null;
      const parsed = Number(trimmed);
      return Number.isNaN(parsed) ? trimmed : parsed;
    }
    return null;
  }

  if (field.uiControl === 'table') {
    if (!Array.isArray(value)) return null;
    // Strip rows where every cell is empty - those are placeholder rows only.
    const nonEmpty = (value as Array<Record<string, string>>).filter((row) =>
      Object.values(row).some((v) => typeof v === 'string' && v.trim() !== '')
    );
    return nonEmpty.length > 0 ? nonEmpty : null;
  }

  if (field.uiControl === 'tech_stack_editor') {
    // Structured systems array (parse handles a JSON string too); the canonical snapshot
    // deep-compares it so any add/edit/remove activates the Save button.
    const systems = parseTechStackSystems(value);
    return systems.length > 0 ? systems : null;
  }

  if (
    field.uiControl === 'stack_option' ||
    field.uiControl === 'pill_radio' ||
    field.uiControl === 'select' ||
    field.uiControl === 'date'
  ) {
    const normalized = normalizeAnswerChoice(value);
    return normalized ?? null;
  }

  const normalizedText = normalizeTextValue(value).trim();
  return normalizedText || null;
}

// ── Rule-driven evaluation (replaces hard-coded switch statements) ──────────

function splitRuleValues(value: string | null | undefined): string[] {
  return (value ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

function toRuleNumber(value: unknown): number | null {
  const raw = Array.isArray(value) ? value[0] : value;
  if (raw == null || (typeof raw === 'string' && raw.trim() === '')) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Evaluate a single conditional rule against the current answer map.
 * Returns true if the condition is satisfied.
 */
export function evaluateConditionalRule(
  rule: ConditionalRule,
  answers: QuestionnaireAnswerMap,
  fieldLookup?: QuestionnaireFieldLookup
): boolean {
  const sourceValue = getAnswerValueByFieldKey(rule.sourceFieldKey, answers, fieldLookup);
  switch (rule.sourceOperator) {
    case 'eq':
      return normalizeAnswerChoice(sourceValue) === rule.sourceValue;
    case 'neq':
      return rule.sourceValue === null
        ? sourceValue != null
        : normalizeAnswerChoice(sourceValue) !== rule.sourceValue;
    case 'includes':
      return normalizeMultiSelectValue(sourceValue).includes(rule.sourceValue ?? '');
    case 'not_includes':
      return !normalizeMultiSelectValue(sourceValue).includes(rule.sourceValue ?? '');
    case 'any_selected': {
      const sv = sourceValue;
      if (sv == null) return false;
      if (typeof sv === 'string') return sv.trim().length > 0;
      if (Array.isArray(sv)) return (sv as unknown[]).length > 0;
      return Boolean(sv);
    }
    case 'none_selected': {
      const sv = sourceValue;
      if (sv == null) return true;
      if (typeof sv === 'string') return sv.trim().length === 0;
      if (Array.isArray(sv)) return (sv as unknown[]).length === 0;
      return !sv;
    }
    case 'in': {
      const choice = normalizeAnswerChoice(sourceValue);
      return choice != null && splitRuleValues(rule.sourceValue).includes(choice);
    }
    case 'gt': {
      const n = toRuleNumber(sourceValue);
      const limit = toRuleNumber(rule.sourceValue);
      return n != null && limit != null && n > limit;
    }
    default:
      return true;
  }
}

/**
 * Determine if a field is visible based on server-provided conditional rules.
 *
 * - Fields with no `show` rules are always visible.
 * - When multiple `show` rules exist for a target, ALL must match (AND semantics).
 * - `hide` rules (if any) hide the field when any one matches (OR semantics).
 */
export function isFieldVisibleByRules(
  fieldKey: string,
  rules: ConditionalRule[],
  answers: QuestionnaireAnswerMap,
  fieldLookup?: QuestionnaireFieldLookup
): boolean {
  const showRules = rules.filter((r) => r.targetFieldKey === fieldKey && r.effect === 'show');
  const hideRules = rules.filter((r) => r.targetFieldKey === fieldKey && r.effect === 'hide');

  if (hideRules.some((r) => evaluateConditionalRule(r, answers, fieldLookup))) return false;
  if (showRules.length === 0) return true;
  return showRules.every((r) => evaluateConditionalRule(r, answers, fieldLookup));
}

/**
 * True when a group HAS fields but the conditional rules hide every one of them (e.g. Maxio's
 * "Usage Metering" group when revenue has no usage). Such a group is not rendered as an empty
 * panel. A group with no fields at all (a fresh "Additional Information" group, an empty ad-hoc
 * section) is NOT conditionally empty - it keeps its "add a question" hint.
 */
export function isGroupHiddenByRules(
  fieldKeys: readonly string[],
  rules: ConditionalRule[],
  answers: QuestionnaireAnswerMap,
  fieldLookup?: QuestionnaireFieldLookup
): boolean {
  return (
    fieldKeys.length > 0 &&
    fieldKeys.every((k) => !isFieldVisibleByRules(k, rules, answers, fieldLookup))
  );
}

/**
 * Get the nesting (indentation) level of a field from server-provided conditional rules.
 * Returns 0 for fields with no rules.
 */
export function getFieldNestingLevelByRules(fieldKey: string, rules: ConditionalRule[]): number {
  const rule = rules.find((r) => r.targetFieldKey === fieldKey);
  return rule?.nestingLevel ?? 0;
}

/**
 * Determine if a field is enabled based on server-provided conditional rules.
 *
 * - Fields with no `enable` rules are always enabled.
 * - When one or more `enable` rules exist for a target, ALL must match (AND semantics).
 */
export function isFieldEnabledByRules(
  fieldKey: string,
  rules: ConditionalRule[],
  answers: QuestionnaireAnswerMap,
  fieldLookup?: QuestionnaireFieldLookup
): boolean {
  const enableRules = rules.filter((r) => r.targetFieldKey === fieldKey && r.effect === 'enable');
  if (enableRules.length === 0) return true;
  return enableRules.every((r) => evaluateConditionalRule(r, answers, fieldLookup));
}
